import React, { useEffect, useRef, useState } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import Form from '@rjsf/core';
import { getDefaultFormState, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Box, Button, Grid } from '@mui/material';
import type { JobDraft, Workflow } from '@/redux/slices/slice_types';
import { Modal } from '@/components/_common';
import type { ProfileDetailsFormHandleChange, ProfileDetailsFormProfile } from './OPJobAccordion/OPJobProfileDetailsForm';
import OPJobProfileDetailsForm from './OPJobAccordion/OPJobProfileDetailsForm';
import { useAppSelector } from '@/redux/hooks';
import { selectWorkflows } from '@/redux/selectors/profileSelectors';
import { JobBulkEditFormData } from '@/components/OrderPlacement/OrderPlacement.types';
import { applyTemplateToOrderProfile, applyTemplateToOrderSpec } from '@/_utils/orderUtils';

interface Props {
  jobs: JobDraft[];
  totalJobsCount: number;
  isOpen: boolean;
  onClose: () => void;
  onApply: (formData: JobBulkEditFormData) => void;
}

type JobDraftWithProfile = Omit<JobDraft, 'profile'> & { profile: NonNullable<JobDraft['profile']> };

const allJobDraftsHaveProfile = (jobDrafts: JobDraft[]): jobDrafts is JobDraftWithProfile[] => {
  return jobDrafts.every((item): item is JobDraftWithProfile => item.profile !== null && item.profile !== undefined);
};

const isFieldEqualForAllJobs = (jobDrafts: JobDraft[], field: keyof JobDraft) => {
  return jobDrafts.every((jobDraft) => isEqual(jobDrafts[0][field], jobDraft[field]));
};

// Builds a single “shared” profile value to prefill the bulk-edit modal.
// Returns the profile only when all selected jobs have the same profile id; if some nested fields differ (e.g. resolution/tracks), it clears them to indicate “mixed values”.
const getBulkEditModalProfileData = (jobs: JobDraft[]): ProfileDetailsFormProfile | undefined => {
  if (!jobs[0].profile) {
    return undefined;
  }

  // If there is only one job selected then return data from that job
  if (jobs.length === 1) {
    return cloneDeep(jobs[0].profile);
  }

  const isAllJobsHaveProfile = allJobDraftsHaveProfile(jobs);
  if (!isAllJobsHaveProfile) return undefined;

  const isJobsHaveSameProfile = jobs.every((job, _, jobsArray) => job.profile.id === jobsArray[0].profile.id);
  if (!isJobsHaveSameProfile) return undefined;

  const profilesDataArray = jobs.map((job) => job.profile.data);
  const mediaResolutionsEqual = profilesDataArray.every((profilesData) => profilesData.media_resolution === profilesDataArray[0].media_resolution);
  const media_resolution = mediaResolutionsEqual ? profilesDataArray[0].media_resolution : undefined;
  const audioTracksEqual = profilesDataArray.every((profilesData) => isEqual(profilesData.audio_tracks, profilesDataArray[0].audio_tracks));
  const audio_tracks = audioTracksEqual ? profilesDataArray[0].audio_tracks : [];
  const subtitleTracksEqual = profilesDataArray.every((profilesData) => isEqual(profilesData.subtitle_tracks, profilesDataArray[0].subtitle_tracks));
  const subtitle_tracks = subtitleTracksEqual ? profilesDataArray[0].subtitle_tracks : [];

  const profile = cloneDeep(jobs[0].profile);
  profile.data.media_resolution = media_resolution;
  profile.data.audio_tracks = audio_tracks;
  profile.data.subtitle_tracks = subtitle_tracks;

  return profile;
};

const getSpecs = (workflows: Record<string, Workflow>, profile?: ProfileDetailsFormProfile) => {
  const workflowName = profile?.data?.workflow;
  const orderProfileSchema: RJSFSchema | undefined = workflowName ? workflows[workflowName]?.order_profile_schema : undefined;
  const orderSpecSchema: RJSFSchema | undefined = workflowName ? workflows[workflowName]?.order_spec_schema : undefined;

  return {
    orderProfileSchema,
    orderSpecSchema
  };
};

type ValidationErrors = Partial<Record<'customer' | 'profile' | 'due_date', boolean>>;

// The term “default” refers to a spec form input with `template_inputs` applied.
// For example, in the case with the order profile form, that would mean `order_profile_inputs` with
// `template_inputs` applied as overrides.
type UseDefaultInputs = boolean;

const OPJobBulkEditModal: React.FC<Props> = ({ jobs, totalJobsCount, isOpen, onClose, onApply }) => {
  const workflows = useAppSelector(selectWorkflows);

  const [initialFormState, setInitialFormState] = useState<Partial<JobBulkEditFormData> | null>(null); // form state that is assigned to the form when the modal is opened
  const [formState, setFormState] = useState<Partial<JobBulkEditFormData>>({});

  const [usedDefaultOrderProfileInputs, setUsedDefaultOrderProfileInputs] = useState<UseDefaultInputs | null>(null);
  const [usedDefaultOrderSpecInputs, setUsedDefaultOrderSpecInputs] = useState<UseDefaultInputs | null>(null);

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const orderProfileSchemaFormRef = useRef<Form | null>(null);

  const { orderProfileSchema, orderSpecSchema } = getSpecs(workflows, formState.profile);

  useEffect(() => {
    // When the modal opens, we build the initial bulk-edit form state
    if (isOpen) {
      const profile = getBulkEditModalProfileData(jobs);
      const templateInputs = profile?.data.template_inputs;
      const { orderProfileSchema, orderSpecSchema } = getSpecs(workflows, profile);

      const isOrderProfileInputsEqual = isFieldEqualForAllJobs(jobs, 'order_profile_inputs');
      const isOrderSpecInputsEqual = isFieldEqualForAllJobs(jobs, 'order_spec_inputs');
      const shouldUseDefaultOrderProfileInputs: UseDefaultInputs = Boolean(profile) && !isOrderProfileInputsEqual;
      const shouldUseDefaultOrderSpecInputs: UseDefaultInputs = Boolean(profile) && !isOrderSpecInputsEqual;

      const order_profile_inputs = !profile
        ? undefined
        : shouldUseDefaultOrderProfileInputs
          ? applyTemplateToOrderProfile({
              templateInputs,
              currentOrderProfileInputs: undefined,
              orderProfileSchema
            }).updatedOrderProfileInputs
          : jobs[0].order_profile_inputs;

      const order_spec_inputs = !profile
        ? undefined
        : shouldUseDefaultOrderSpecInputs
          ? applyTemplateToOrderSpec({
              templateInputs,
              currentOrderSpecInputs: undefined,
              orderSpecSchema
            }).updatedOrderSpecInputs
          : jobs[0].order_spec_inputs;

      const initialFormState = {
        customer: isFieldEqualForAllJobs(jobs, 'customer') ? jobs[0].customer : undefined,
        due_date: jobs[0].due_date && isFieldEqualForAllJobs(jobs, 'due_date') ? jobs[0].due_date : undefined,
        embargo_date: jobs[0].embargo_date && isFieldEqualForAllJobs(jobs, 'embargo_date') ? jobs[0].embargo_date : undefined,
        profile,
        // `getDefaultFormState` normalizes inputs to the schema-shaped formData: applies schema defaults and fill missing nested fields.
        // Otherwise, the initial vs. current formData comparison in the `handleApplyButtonClick` will not work
        // (without calling `getDefaultFormState` initial `order_profile_inputs` might be undefined or something like "{}").
        order_profile_inputs: orderProfileSchema
          ? getDefaultFormState(validator, orderProfileSchema, order_profile_inputs, orderProfileSchema)
          : undefined,
        order_spec_inputs: orderSpecSchema ? getDefaultFormState(validator, orderSpecSchema, order_spec_inputs, orderSpecSchema) : undefined
      };

      setUsedDefaultOrderProfileInputs(shouldUseDefaultOrderProfileInputs);
      setUsedDefaultOrderSpecInputs(shouldUseDefaultOrderSpecInputs);
      setInitialFormState(initialFormState);
      setFormState(initialFormState);
    } else {
      // reset field when modal closed
      setFormState({});
      setInitialFormState({});
      setUsedDefaultOrderProfileInputs(null);
      setUsedDefaultOrderSpecInputs(null);
    }
  }, [isOpen]);

  const handleChange: ProfileDetailsFormHandleChange = ({ key, value }) => {
    setFormState({
      ...formState,
      [key]: value,
      ...(key === 'customer' ? { profile: undefined, order_spec_inputs: undefined } : {}),
      ...(key === 'profile' ? { order_spec_inputs: undefined } : {})
    });

    if (value) {
      setValidationErrors({ ...validationErrors, [key]: false }); // unset error flag if value not empty
    }
  };

  const handleOrderSpecInputUpdate = (inputs: Record<string, any> | undefined) => {
    setFormState({
      ...formState,
      order_spec_inputs: inputs
    });
  };

  const handleApplyButtonClick = () => {
    let errors: ValidationErrors = {};
    const { customer, profile, due_date, embargo_date, order_profile_inputs, order_spec_inputs } = formState;

    if (!customer) errors = { ...errors, customer: true };
    if (!profile) errors = { ...errors, profile: true };
    if (!due_date) errors = { ...errors, due_date: true };

    if (!customer || !profile || !due_date) {
      setValidationErrors(errors);
      return;
    }

    const orderProfileSchemaHasProperties = Object.keys(orderProfileSchema?.properties || {}).length;
    if (orderProfileSchemaHasProperties && !orderProfileSchemaFormRef.current?.validateForm()) {
      return;
    }

    const data: JobBulkEditFormData = {
      customer,
      profile,
      due_date,
      embargo_date: embargo_date ?? null
    };

    // If the "default" inputs were used, we want to apply `order_profile_inputs` changes to all jobs only if
    // users actually changed any value inside the order profile schema form
    if (usedDefaultOrderProfileInputs) {
      if (!isEqual(initialFormState?.order_profile_inputs, order_profile_inputs)) {
        data.order_profile_inputs = order_profile_inputs;
      }
    } else {
      data.order_profile_inputs = order_profile_inputs;
    }

    // If the "default" inputs were used, we want to apply `order_spec_inputs` changes to all jobs only if
    // users actually changed any value inside the order spec schema form
    if (usedDefaultOrderSpecInputs) {
      if (!isEqual(initialFormState?.order_spec_inputs, order_spec_inputs)) {
        data.order_spec_inputs = order_spec_inputs;
      }
    } else {
      data.order_spec_inputs = order_spec_inputs;
    }

    onApply(data);
    onClose();
  };

  const modalTitle = `Editing ${jobs.length} of ${totalJobsCount} job${totalJobsCount === 1 ? '' : 's'}`;

  return (
    <Modal open={isOpen} title={modalTitle} maxWidth={2000} onClose={onClose}>
      <Box sx={{ maxHeight: 'calc(100dvh - 300px)', overflow: 'auto' }}>
        <OPJobProfileDetailsForm
          job={formState}
          isInsideModal
          orderProfileForm={{
            register: false,
            formRef: orderProfileSchemaFormRef,
            schema: orderProfileSchema,
            inputs: formState.order_profile_inputs,
            beforeMessage: usedDefaultOrderProfileInputs && specFormInputsNotEqualMessage
          }}
          orderSpecForm={{
            schema: orderSpecSchema,
            inputs: formState.order_spec_inputs,
            beforeMessage: usedDefaultOrderSpecInputs && specFormInputsNotEqualMessage
          }}
          validationErrors={validationErrors}
          updateOrderSpecInputs={handleOrderSpecInputUpdate}
          handleChange={handleChange}
        />
      </Box>
      <Grid container justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button onClick={onClose} color="default" variant="text" sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button onClick={handleApplyButtonClick} color="primary" variant="contained">
          Apply
        </Button>
      </Grid>
    </Modal>
  );
};

const specFormInputsNotEqualMessage = (
  <Box mb={2} fontStyle={'italic'} color="warning.main">
    Due to differences between inputs in the selected jobs, we have applied the default values for this section. Changes to this section will only
    apply if updated.
  </Box>
);

export default OPJobBulkEditModal;
